import AxiosConfig from '../utils/AxiosConfig';

class BillingStatementService {
  
  // Obtener todos los estados de cuenta
  async getBillingStatements(page = 1, pageSize = 10, searchTerm = '', options = {}) {
    try {
      const {
        sortField,
        sortDirection,
        filterField,
        filterOperator,
        filterValue,
        status,
        clientId
      } = options;

      const params = new URLSearchParams({
        page: page.toString(),
        pageSize: pageSize.toString()
      });

      if (searchTerm && typeof searchTerm === 'string' && searchTerm.trim()) {
        params.append('search', searchTerm.trim());
      }
      if (sortField) params.append('sortField', sortField);
      if (sortDirection) params.append('sortDirection', sortDirection);
      if (filterField) params.append('filterField', filterField);
      if (filterOperator) params.append('filterOperator', filterOperator);
      if (filterValue !== undefined && filterValue !== null && String(filterValue).trim() !== '') {
        params.append('filterValue', String(filterValue).trim());
      }
      if (status) params.append('status', status);
      if (clientId) params.append('clientId', String(clientId));

      const response = await AxiosConfig.get(`/b2b/billing-statements?${params}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  // Generar PDF del estado de cuenta
  async generateStatementPDF(statementId) {
    try {
      console.log('🔍 BillingStatementService - Generando PDF para statement:', statementId);
      const response = await AxiosConfig.get(`/b2b/billing-statements/${statementId}/export/pdf`, {
        responseType: 'blob',
      });
      console.log('✅ BillingStatementService - PDF generado exitosamente');
      return response.data;
    } catch (error) {
      console.error('❌ BillingStatementService - Error generando PDF:', error);
      throw error;
    }
  }

  // Obtener detalles de un estado de cuenta específico
  async getBillingStatementById(statementId) {
    try {
      const response = await AxiosConfig.get(`/b2b/billing-statements/${statementId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching billing statement:', error);
      throw error;
    }
  }

  // Movimientos (pedidos y pagos) con saldo acumulado, paginados en el backend
  async getClientMovements(clientId, page = 1, pageSize = 20) {
    try {
      const params = new URLSearchParams({ page, pageSize });
      const response = await AxiosConfig.get(`/b2b/billing-statements/${clientId}/movements?${params.toString()}`);
      return response.data?.data || response.data;
    } catch (error) {
      console.error('Error fetching client movements:', error);
      throw error;
    }
  }

  // Obtener estados de cuenta por cliente
  async getBillingStatementsByClient(clientId) {
    try {
      const response = await AxiosConfig.get(`/b2b/clients/${clientId}/billing-statements`);
      return response.data;
    } catch (error) {
      console.error('Error fetching client billing statements:', error);
      throw error;
    }
  }

  // Generar estado de cuenta para un cliente
  async generateBillingStatement(clientId, periodData) {
    try {
      const response = await AxiosConfig.post(`/b2b/clients/${clientId}/billing-statements`, periodData);
      return response.data;
    } catch (error) {
      console.error('Error generating billing statement:', error);
      throw error;
    }
  }

  // Obtener estados de cuenta por período
  async getBillingStatementsByPeriod(startDate, endDate) {
    try {
      const response = await AxiosConfig.get(`/b2b/billing-statements/period`, {
        params: {
          startDate,
          endDate
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching billing statements by period:', error);
      throw error;
    }
  }

  // Exportar estado de cuenta a PDF
  async exportBillingStatementToPDF(statementId) {
    try {
      const response = await AxiosConfig.get(`/b2b/billing-statements/${statementId}/export/pdf`, {
        responseType: 'blob'
      });
      return response.data;
    } catch (error) {
      console.error('Error exporting billing statement to PDF:', error);
      throw error;
    }
  }


  // Obtener estados de cuenta disponibles
  getBillingStatementStatuses() {
    return [
      { value: 'UP_TO_DATE', label: 'Al día' },
      { value: 'PENDING_DEBT', label: 'Deuda pendiente' },
      { value: 'OVERDUE_DEBT', label: 'Deuda vencida' }
    ];
  }

  // Calcular resumen de estados de cuenta
  calculateBillingSummary(statements) {
    const summary = {
      totalCompanies: statements.length,
      totalAmount: 0,
      totalPaid: 0,
      totalPending: 0,
      upToDate: 0,
      pendingDebt: 0,
      overdueDebt: 0
    };

    statements.forEach(statement => {
      summary.totalAmount += statement.totalAmount || 0;
      summary.totalPaid += statement.paidAmount || 0;
      summary.totalPending += statement.pendingAmount || 0;

      switch (statement.status) {
        case 'UP_TO_DATE':
          summary.upToDate++;
          break;
        case 'PENDING_DEBT':
          summary.pendingDebt++;
          break;
        case 'OVERDUE_DEBT':
          summary.overdueDebt++;
          break;
      }
    });

    return summary;
  }

  // Validar datos de estado de cuenta
  validateBillingStatementData(statementData) {
    const errors = {};

    if (!statementData.clientId) {
      errors.clientId = 'El cliente es requerido';
    }

    if (!statementData.period) {
      errors.period = 'El período es requerido';
    }

    return {
      isValid: Object.keys(errors).length === 0,
      errors
    };
  }

  // Formatear fecha para el período
  formatPeriodDate(date) {
    if (!date) return '';
    
    const d = new Date(date);
    const month = d.toLocaleString('es-ES', { month: 'long' });
    const year = d.getFullYear();
    
    return `${month.charAt(0).toUpperCase() + month.slice(1)} ${year}`;
  }

  // Enviar estado de cuenta por correo
  async sendBillingStatementEmail(statementId, email) {
    try {
      const response = await AxiosConfig.post(`/b2b/billing-statements/${statementId}/send-email`, {
        email: email
      });
      return response.data;
    } catch (error) {
      console.error('Error sending billing statement email:', error);
      throw error;
    }
  }

  // Obtener color del estado
  getStatusColor(status) {
    switch (status) {
      case 'UP_TO_DATE':
        return 'success';
      case 'PENDING_DEBT':
        return 'warning';
      case 'OVERDUE_DEBT':
        return 'error';
      default:
        return 'default';
    }
  }
}

export default new BillingStatementService();
